<template>
  <b-col cols="11" lg="8" class="contact-details">
    <b-row class="justify-content-between align-items-center mb-5">
      <section v-if="errored">
        <p class="m-0">{{ fmxSorry }}</p>
      </section>
      <template v-else>
        <div v-if="loading">
          <p class="m-0">{{ fmxLoading }}</p>
        </div>
        <template v-else>
          <b-col cols="11" lg="12" class="mb-5" v-if="contactUsLabel">
            <h2 class="promo-title text-center m-0">{{ contactUsLabel }}</h2>
          </b-col>
          <b-col cols="12" lg="6" class="mb-3 mb-lg-0">
            <h2 class="mb-4" v-if="addressLabel">{{ addressLabel }}</h2>
            <address class="mb-5">
              <div v-if="data.address_1" v-html="data.address_1"></div>
              <div v-if="data.address_2" v-html="data.address_2"></div>
              <div v-if="data.address_3" v-html="data.address_3"></div>
              <div v-if="data.city" v-html="data.city"></div>
              <div v-if="data.county" v-html="data.county"></div>
              <div v-if="data.postcode" v-html="data.postcode"></div>
            </address>
            <p class="mb-5">
              <a
                :href="mapLink"
                target="_blank"
                class="get-directions-link"
                v-if="mapLink"
              >
                <font-awesome-icon
                  :icon="['fal', 'map-marker']"
                  size="2x"
                  class="float-left mr-3"
                ></font-awesome-icon>
                {{ getDirectionsLabel }}
              </a>
              <a
                :href="
                  'https://maps.google.com/maps?q=' +
                  data.latitude +
                  ',' +
                  data.longitude
                "
                target="_blank"
                class="get-directions-link"
                v-else
              >
                <font-awesome-icon
                  :icon="['fal', 'map-marker']"
                  size="2x"
                  class="float-left mr-3"
                ></font-awesome-icon>
                {{ getDirectionsLabel }}
              </a>
            </p>
            <p class="mb-5" v-if="normalisedTelephone">
              <a :href="'tel:' + normalisedTelephone">
                <font-awesome-icon
                  :icon="['fal', 'phone']"
                  size="2x"
                  class="float-left mr-3"
                ></font-awesome-icon>
                {{ normalisedTelephone }}
              </a>
            </p>
            <p class="mb-5" v-if="data.emails[0]">
              <a :href="'mailto:' + data.emails[0]">
                <font-awesome-icon
                  :icon="['fal', 'envelope']"
                  size="2x"
                  class="float-left mr-3"
                ></font-awesome-icon>
                {{ data.emails[0] }}
              </a>
            </p>
            <p class="m-0" v-if="data.whatsappnumber">
              <a
                :href="
                  'https://wa.me/' + data.whatsappnumber.replace(/[^\d]/g, '')
                "
                class="d-flex align-items-center"
              >
                <font-awesome-icon
                  :icon="['fab', 'whatsapp']"
                  size="2x"
                  class="float-left mr-3"
                ></font-awesome-icon>
                WhatsApp Us
              </a>
            </p>
          </b-col>
          <b-col cols="12" lg="6" class="content-green">
            <h3 class="h2 mb-4" v-if="tableLabel">{{ tableLabel }}</h3>
            <div class="table-responsive">
              <table class="table table-borderless mb-0">
                <tr v-for="day in data.opening_days.data" :key="day">
                  <td v-html="day.name"></td>
                  <td
                    class="text-right"
                    v-html="locationOpen"
                    v-if="day.is_24h === 1"
                  ></td>
                  <td
                    class="text-right"
                    v-html="locationClosed"
                    v-else-if="day.is_closed === 1"
                  ></td>
                  <td
                    class="text-right"
                    v-html="
                      new Date('1970-01-01 ' + day.open).toLocaleTimeString('en', {
                        hour12: acceptLanguage === 'en-US',
                        hour: 'numeric',
                        minute: 'numeric',
                      }) +
                      ' - ' +
                      new Date('1970-01-01 ' + day.close).toLocaleTimeString('en', {
                        hour12: acceptLanguage === 'en-US',
                        hour: 'numeric',
                        minute: 'numeric',
                      })"
                    v-else
                  ></td>
                </tr>
              </table>
            </div>
          </b-col>
        </template>
      </template>
    </b-row>
  </b-col>
</template>

<script>
import axios from "axios";
import {
  normaliseCountryCode,
  normalisePhoneNumber,
} from "../utilities/telephone";

export default {
  name: "FmxContact",
  props: {
    acceptLanguage: {
      type: String,
      default: null,
    },
    fmxSorry: {
      type: String,
      default: null,
    },
    fmxLoading: {
      type: String,
      default: null,
    },
    fmxUrl: {
      type: String,
      default: null,
    },
    locationId: {
      type: String,
      default: null,
    },
    contactUsLabel: {
      type: String,
      default: null,
    },
    addressLabel: {
      type: String,
      default: null,
    },
    tableLabel: {
      type: String,
      default: null,
    },
    getDirectionsLabel: {
      type: String,
      default: null,
    },
    mapLink: {
      type: String,
      default: null,
    },
    countryCode: {
      type: String,
      default: null,
    },
    locationOpen: {
      type: String,
      default: null,
    },
    locationClosed: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      data: null,
      loading: true,
      errored: false,
      shouldMergeIntoSeomatic: window.shouldMergeIntoSeomatic || false,
    };
  },
  computed: {
    normalisedTelephone() {
      if (!this.data) return undefined;

      return normalisePhoneNumber(
        this.data.telephone,
        this.countryCode || this.data.country_code || this.data.country
      );
    },
  },
  mounted() {
    axios
      .get(
        this.fmxUrl + "/locations/" + this.locationId + "?include=opening_days",
        {
          headers: {
            "Accept-Language": this.acceptLanguage,
          },
        }
      )
      .then((response) => {
        this.data = response.data.data;
      })
      .catch((error) => {
        console.log(error);
        this.errored = true;
      })
      .finally(() => (this.loading = false));
  },
  methods: {
    // Convert the API response into schema.org-compatible fragments
    buildSchemaPartsFromData() {
      const d = this.data;
      if (!d) return {};
      const countryCode = normaliseCountryCode(
        this.countryCode || d.country_code || d.country
      );

      // Helper: normalise time strings to HH:mm
      const toHm = (t) => {
        if (!t) return null;
        const [h = "00", m = "00"] = String(t).split(":");
        return `${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
      };

      // Helper: map day names to schema.org values
      const dayMap = {
        monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday",
        thursday: "Thursday", friday: "Friday", saturday: "Saturday", sunday: "Sunday",
      };
      const toDayName = (name) => {
        const k = String(name || "").toLowerCase();
        return dayMap[k] || name || null;
      };

      // Build the openingHoursSpecification array from your API data
      const openingHoursSpecification = Array.isArray(d.opening_days?.data)
        ? d.opening_days.data.map((day) => {
            if (day.is_24h === 1) {
              // 24-hour opening
              return {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: [toDayName(day.name)].filter(Boolean),
                opens: "00:00",
                closes: "23:59",
              };
            }
            if (day.is_closed === 1) return null; // closed – skip the day
            const opens = toHm(day.open);
            const closes = toHm(day.close);
            if (!opens || !closes) return null;
            return {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: [toDayName(day.name)].filter(Boolean),
              opens,
              closes,
            };
          }).filter(Boolean)
        : [];

      // Combine street address lines
      const streetAddress = [d.address_1, d.address_2, d.address_3].filter(Boolean).join(", ") || undefined;
      const email = Array.isArray(d.emails) && d.emails.length ? d.emails[0] : undefined;
      const hasCoords = Number.isFinite(d.latitude) && Number.isFinite(d.longitude);

      const url = window.location.href.replace(/#.*$/, "");
      const name =
        this.schemaName ||
        (document?.title ? `Green Motion – ${document.title}` : "Green Motion – Location");

      return {
        _identity: { url, name }, // internal key used for matching
        telephone: this.normalisedTelephone,
        email,
        address: {
          "@type": "PostalAddress",
          streetAddress,
          addressLocality: d.city || undefined,
          addressRegion: d.county || undefined,
          postalCode: d.postcode || undefined,
          addressCountry: countryCode || "GB",
        },
        openingHoursSpecification: openingHoursSpecification.length ? openingHoursSpecification : undefined,
        hasMap: this.mapLink?.length
          ? this.mapLink
          : hasCoords
          ? `https://www.google.com/maps?q=${d.latitude},${d.longitude}`
          : undefined,
        geo: hasCoords
          ? { "@type": "GeoCoordinates", latitude: d.latitude, longitude: d.longitude }
          : undefined,
        "@type": "AutoRental",
        "@id": url + "#location",
        name,
        url,
      };
    },

    // Deep merge utility: prefers truthy values from the right-hand object
    deepMergePreferRight(base, right) {
      if (Array.isArray(base) || Array.isArray(right)) {
        // For arrays such as openingHoursSpecification, replace entirely if new data exists
        return right ?? base;
      }
      if (typeof base === "object" && base && typeof right === "object" && right) {
        const out = { ...base };
        for (const k of Object.keys(right)) {
          const bv = base[k];
          const rv = right[k];
          out[k] = this.deepMergePreferRight(bv, rv);
        }
        return out;
      }
      return right ?? base;
    },

    // Locate the SEOmatic <script> containing an @graph
    findSeomaticGraphScript() {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      for (const s of scripts) {
        try {
          const json = JSON.parse(s.textContent.trim());
          if (json && typeof json === "object" && Array.isArray(json["@graph"])) {
            return { el: s, json };
          }
        } catch (_) {}
      }
      return null;
    },

    // Pick the relevant business node inside the graph
    pickLocationNodeFromGraph(graph, currentUrl) {
      const isCandidate = (node) => {
        if (!node || typeof node !== "object") return false;
        const types = Array.isArray(node["@type"]) ? node["@type"] : [node["@type"]];
        const looksLikeBiz = types.includes("AutoRental") || types.includes("LocalBusiness");
        if (!looksLikeBiz) return false;
        const urlMatch =
          (node.url && String(node.url).replace(/#.*$/, "") === currentUrl) ||
          (typeof node.mainEntityOfPage === "string" && node.mainEntityOfPage === currentUrl) ||
          (node.mainEntityOfPage && node.mainEntityOfPage["@id"] === currentUrl);
        return urlMatch;
      };

      const url = currentUrl;
      // Prefer AutoRental, otherwise fall back to LocalBusiness
      let best = graph.find((n) => {
        const t = n?.["@type"];
        const type = Array.isArray(t) ? t : [t];
        return type.includes("AutoRental") && isCandidate(n);
      });
      if (best) return best;
      best = graph.find((n) => isCandidate(n));
      return best || null;
    },

    // Main merge operation
    mergeIntoSeomaticJsonLd() {
      const parts = this.buildSchemaPartsFromData();
      if (!parts || !parts._identity) return;

      // Case 1: a JSON-LD graph already exists – update it
      const found = this.findSeomaticGraphScript();
      if (found) {
        const { el, json } = found;
        const currentUrl = parts._identity.url;

        json["@context"] = json["@context"] || "https://schema.org";

        let node = this.pickLocationNodeFromGraph(json["@graph"], currentUrl);

        if (node) {
          if (typeof node.mainEntityOfPage === "string") {
            node.mainEntityOfPage = { "@type": "WebPage", "@id": node.mainEntityOfPage };
          }

          // Merge fields without discarding other data SEOmatic produced
          const merged = this.deepMergePreferRight(node, parts);
          delete merged._identity;
          const idx = json["@graph"].indexOf(node);
          if (idx >= 0) json["@graph"][idx] = merged;
        } else {
          // No matching node – append a new one
          const toAdd = { ...parts };
          delete toAdd._identity;
          json["@graph"].push(toAdd);
        }

        // Rewrite the script content
        el.textContent = JSON.stringify(json);
        return;
      }

      // Case 2: no @graph present – add our own separate script
      const scriptId = "gm-location-jsonld";
      const old = document.getElementById(scriptId);
      if (old) old.remove();

      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = scriptId;
      const toWrite = { "@context": "https://schema.org", ...parts };
      delete toWrite._identity;
      el.textContent = JSON.stringify(toWrite);
      document.head.appendChild(el);
    },
  },
  watch: {
    // Once new data arrives, merge it into the SEOmatic JSON-LD
    data() {
      if (this.shouldMergeIntoSeomatic) {
        this.$nextTick(() => this.mergeIntoSeomaticJsonLd());
      }
    },
  },
};
</script>
